/**
 * Segmentation Mapping Types — Frontend Mirror
 *
 * Mirrors the backend SegmentationMapping interface from
 * `apps/backend/src/populations/types/segmentation-mapping.types.ts`.
 *
 * Each mapping links a raw uploaded column to an optional canonical variable
 * from the 21-variable dictionary (B2B/B2C demographics, firmographics,
 * geography, and professional attributes).
 *
 * Consumed by:
 *  - Results page (`features/studies/report/page/StudyReportPage.tsx`) — fallback logic
 *  - ReportLandingPage — prop passthrough
 *  - ReportDashboard — prop passthrough
 *  - PopulationAnalysis — display labels + data access
 *  - Wizard MappingReviewStep (Sprint 2)
 *  - Audience Details editor (Sprint 2)
 *
 * See docs/intelligent-mapping-engine-plan.md for the full architecture.
 */

/**
 * How a column mapping was created:
 * - 'rule'   — Matched via the multi-language rule-based dictionary
 * - 'llm'    — Mapped by the LLM semantic classifier (gpt-5-nano)
 * - 'manual' — Assigned manually by the user in the wizard or details page
 */
export type MappingSource = 'rule' | 'llm' | 'manual'

/**
 * Category groups for canonical segmentation variables.
 * Derived from the canonical ID prefix (e.g. 'std_geo_*' → 'geography').
 */
export type SegmentationCategory =
  | 'geography'
  | 'demographics'
  | 'firmographics'
  | 'professional'

/**
 * A single mapping between a raw uploaded column and a canonical variable.
 *
 * @example Rule-matched:
 * { sourceColumn: 'Country_of_Residence', canonicalId: 'std_geo_country', displayLabel: 'Country', mappingSource: 'rule' }
 *
 * @example LLM-matched:
 * { sourceColumn: 'Q3_work_dept', canonicalId: 'std_prof_function', displayLabel: 'Job Function', mappingSource: 'llm', confidence: 0.85 }
 *
 * @example Unmapped:
 * { sourceColumn: 'custom_score', canonicalId: null, displayLabel: 'custom_score', mappingSource: 'manual' }
 */
export interface SegmentationMapping {
  /** Raw column name from the uploaded file (e.g. 'Country_of_Residence') */
  sourceColumn: string

  /** Canonical variable ID (e.g. 'std_geo_country'), or null if unmapped/custom */
  canonicalId: string | null

  /** Clean display name shown in the UI (e.g. 'Country') */
  displayLabel: string

  /**
   * User alias for the question *group* this column belongs to (set only on group
   * member columns). Distinct from {@link displayLabel}, which names the column
   * itself. Filter UIs prefer this as the group title.
   */
  groupDisplayLabel?: string

  /** How this mapping was created */
  mappingSource: MappingSource

  /** LLM confidence score (0–1). Present only for 'llm' mappings */
  confidence?: number

  /**
   * Whether this mapping uses a user-defined custom variable (not from the
   * 21-variable canonical dictionary). Custom variables have `canonicalId: null`
   * and a user-provided `displayLabel`. They are excluded from category grouping.
   *
   * @default false — absent or false means it's a standard canonical variable.
   */
  isCustom?: boolean

  /**
   * Optional technical flag for system/operational columns (id/routing/meta).
   * When true, UIs should keep this in technical sections rather than primary
   * customer-facing attribute lists.
   */
  isOperational?: boolean

  /**
   * Optional UX flag used during ingestion QA to mark columns with parser
   * quality issues requiring follow-up.
   */
  flaggedForParsing?: boolean

  /**
   * Original SPSS variable name before column renaming (e.g. "B1_1", "A13").
   * Used by cleanLabel() to strip code prefixes and by groupColumnsBySharedStem()
   * to group multi-select items under shared question stems.
   * Absent for non-SAV files or when not available.
   */
  originalName?: string

  /**
   * Value-level mapping for demographic columns (gender, age, country, ethnicity).
   * Maps raw data values to canonical values for deterministic resolution.
   * 
   * Examples:
   *   Gender:    { "Hombre": "Male", "Mujer": "Female", "1": "Male", "2": "Female" }
   *   Age:       { "1": "18-24", "2": "25-34", "23": "18-24" }
   *   Country:   { "Estados Unidos": "US", "Reino Unido": "GB", "1": "US" }
   *   Ethnicity: { "Blanco": "White", "Negro": "Black or African American" }
   * 
   * Auto-detected in wizard via token/alias matching, LLM-enhanced for unrecognized values,
   * user-editable. Used by name synthesis and image assignment as single source of truth.
   */
  valueMap?: Record<string, string>

  /**
   * File/datamap code → human-readable label (display-only). Keys align with
   * raw codes in `valueMap` / distinct values; does not replace `valueMap` for resolution.
   */
  valueLabels?: Record<string, string>
}

/**
 * Helper: extract the source column names from a SegmentationMapping array.
 * Used for backward-compatible passthrough where components still expect string[].
 */
export function mappingsToColumnNames(
  mappings: SegmentationMapping[],
): string[] {
  return mappings.map((m) => m.sourceColumn)
}

/* -------------------------------------------------------------------------- */
/*  Canonical Variable Dictionary                                              */
/* -------------------------------------------------------------------------- */

/**
 * A single entry in the canonical variable dictionary.
 * Frontend mirror of the Python CanonicalVariable dataclass.
 */
export interface CanonicalVariableEntry {
  id: string
  displayLabel: string
  category: SegmentationCategory
}

/**
 * The 21 canonical segmentation variables — frontend mirror of
 * `packages/survey-engine/survey_engine/utils/canonical_variables.py`.
 *
 * Ordered by category: Geography → Demographics → Firmographics → Professional.
 */
export const CANONICAL_VARIABLES: CanonicalVariableEntry[] = [
  // Geography (3)
  { id: 'std_geo_country', displayLabel: 'Country', category: 'geography' },
  { id: 'std_geo_region', displayLabel: 'Region / State', category: 'geography' },
  { id: 'std_geo_city', displayLabel: 'City', category: 'geography' },

  // Demographics (9)
  { id: 'std_dem_age', displayLabel: 'Age', category: 'demographics' },
  { id: 'std_dem_gender', displayLabel: 'Gender', category: 'demographics' },
  { id: 'std_dem_education', displayLabel: 'Education Level', category: 'demographics' },
  { id: 'std_dem_income', displayLabel: 'Household Income', category: 'demographics' },
  { id: 'std_dem_employment', displayLabel: 'Employment Status', category: 'demographics' },
  { id: 'std_dem_marital', displayLabel: 'Marital Status', category: 'demographics' },
  { id: 'std_dem_children', displayLabel: 'Number of Children', category: 'demographics' },
  { id: 'std_dem_ethnicity', displayLabel: 'Ethnicity', category: 'demographics' },
  { id: 'std_dem_language', displayLabel: 'Language', category: 'demographics' },

  // Firmographics (5)
  { id: 'std_firm_industry', displayLabel: 'Industry', category: 'firmographics' },
  { id: 'std_firm_size', displayLabel: 'Company Size', category: 'firmographics' },
  { id: 'std_firm_revenue', displayLabel: 'Annual Revenue', category: 'firmographics' },
  { id: 'std_firm_type', displayLabel: 'Company Type', category: 'firmographics' },
  { id: 'std_firm_years', displayLabel: 'Years in Business', category: 'firmographics' },

  // Professional (4)
  { id: 'std_prof_function', displayLabel: 'Job Function', category: 'professional' },
  { id: 'std_prof_seniority', displayLabel: 'Seniority Level', category: 'professional' },
  { id: 'std_prof_title', displayLabel: 'Job Title', category: 'professional' },
  { id: 'std_prof_decision', displayLabel: 'Decision-Making Role', category: 'professional' },
]

/** Display-friendly labels for each category. */
export const CATEGORY_LABELS: Record<SegmentationCategory, string> = {
  geography: 'Geography',
  demographics: 'Demographics',
  firmographics: 'Firmographics',
  professional: 'Professional',
}

/** Render order for category groups in dropdowns and tables. */
export const CATEGORY_ORDER: SegmentationCategory[] = [
  'geography',
  'demographics',
  'firmographics',
  'professional',
]

/** Canonical variables grouped by category — pre-computed for dropdown rendering. */
export const CANONICAL_VARIABLES_BY_CATEGORY: Record<SegmentationCategory, CanonicalVariableEntry[]> =
  CATEGORY_ORDER.reduce(
    (acc, cat) => {
      acc[cat] = CANONICAL_VARIABLES.filter((v) => v.category === cat)
      return acc
    },
    {} as Record<SegmentationCategory, CanonicalVariableEntry[]>,
  )

/** Look up a canonical variable by its ID. Returns undefined if not found. */
export function getCanonicalVariable(id: string): CanonicalVariableEntry | undefined {
  return CANONICAL_VARIABLES.find((v) => v.id === id)
}

/** Get a display label for a canonical ID. Returns the ID itself as fallback. */
export function getDisplayLabel(canonicalId: string): string {
  return getCanonicalVariable(canonicalId)?.displayLabel ?? canonicalId
}

/** Derive the category from a canonical ID (prefix-based). Returns null for unknown IDs. */
export function getCategoryForCanonicalId(canonicalId: string | null): SegmentationCategory | null {
  if (!canonicalId) return null
  const entry = getCanonicalVariable(canonicalId)
  return entry?.category ?? null
}

/** Get canonical variables grouped by category (same as CANONICAL_VARIABLES_BY_CATEGORY). */
export function getCanonicalVariablesByCategory(): Record<SegmentationCategory, CanonicalVariableEntry[]> {
  return CANONICAL_VARIABLES_BY_CATEGORY
}

/* -------------------------------------------------------------------------- */
/*  Expected Variables per Audience Type (Sprint 4 — Step 11.3)               */
/* -------------------------------------------------------------------------- */

/**
 * Audience type classification used throughout the platform.
 * - 'B2C' — Business-to-Consumer audiences (demographics-heavy)
 * - 'B2B' — Business-to-Business audiences (firmographics + professional)
 */
export type AudienceType = 'B2B' | 'B2C'

/**
 * Expected canonical variables per audience type.
 *
 * These define the "standard filters" that are typically present in well-structured
 * audience data. The MappingReviewStep uses this to show a "Missing Filters" section
 * when expected variables are not detected or assigned.
 *
 * B2C audiences prioritize consumer demographics: age, gender, country, income, education.
 * B2B audiences prioritize firmographics + professional: industry, company size,
 * seniority, job function, and country (shared).
 *
 * @see docs/intelligent-mapping-engine-plan.md — Step 11.3
 */
export const EXPECTED_VARIABLES_BY_AUDIENCE_TYPE: Record<AudienceType, string[]> = {
  B2C: [
    'std_dem_age',
    'std_dem_gender',
    'std_geo_country',
    'std_dem_income',
    'std_dem_education',
  ],
  B2B: [
    'std_firm_industry',
    'std_firm_size',
    'std_prof_seniority',
    'std_prof_function',
    'std_geo_country',
  ],
}

/**
 * Compute which expected canonical variables are missing from the current mappings.
 *
 * @param mappings     - Current segmentation mappings
 * @param audienceType - The audience classification ('B2B' or 'B2C')
 * @returns Array of CanonicalVariableEntry for expected variables not yet mapped
 */
export function getMissingExpectedVariables(
  mappings: SegmentationMapping[],
  audienceType: AudienceType | '' | undefined,
): CanonicalVariableEntry[] {
  if (!audienceType) return []

  const expectedIds = EXPECTED_VARIABLES_BY_AUDIENCE_TYPE[audienceType as AudienceType]
  if (!expectedIds) return []

  const assignedIds = new Set(
    mappings.filter((m) => m.canonicalId != null).map((m) => m.canonicalId!),
  )

  return expectedIds
    .filter((id) => !assignedIds.has(id))
    .map((id) => getCanonicalVariable(id))
    .filter((v): v is CanonicalVariableEntry => v != null)
}

/* -------------------------------------------------------------------------- */
/*  Mandatory Variables per Audience Type                                       */
/*  Re-exported from @digital-twins/shared-types (single source of truth).     */
/* -------------------------------------------------------------------------- */

import {
  MANDATORY_VARIABLES as _MANDATORY_VARIABLES,
  STRICTLY_REQUIRED_VARIABLES as _STRICTLY_REQUIRED_VARIABLES,
  CONSCIOUS_CHOICE_VARIABLES as _CONSCIOUS_CHOICE_VARIABLES,
  B2B_AGE_FALLBACK_VARIABLES as _B2B_AGE_FALLBACK_VARIABLES,
} from '@digital-twins/shared-types/mandatory-demographics';

export const MANDATORY_VARIABLES = _MANDATORY_VARIABLES;
export const STRICTLY_REQUIRED_VARIABLES = _STRICTLY_REQUIRED_VARIABLES;
export const CONSCIOUS_CHOICE_VARIABLES = _CONSCIOUS_CHOICE_VARIABLES;
export const B2B_AGE_FALLBACK_VARIABLES = _B2B_AGE_FALLBACK_VARIABLES;

/**
 * Maps canonical variable IDs to human-readable constantContext keys.
 *
 * When a user manually provides a value for a mandatory variable (e.g., picks
 * "United States" from the country dropdown), the value is stored in
 * `population.constantContext` using these human-readable keys.
 *
 * These keys are chosen to match the key-hint tuples in `personas.py`
 * (`_COUNTRY_KEY_HINTS`, `_GENDER_KEY_HINTS`, `_AGE_KEY_HINTS`) so that
 * `resolve_demographics()` finds them without any code changes.
 *
 * Example: `{ country: "US", gender: "Female", age: "25-34" }`
 */
export const CANONICAL_TO_CONTEXT_KEY: Record<string, string> = {
  std_geo_country: 'country',
  std_dem_gender: 'gender',
  std_dem_age: 'age',
}

/**
 * Reverse lookup: constantContext key → canonical ID.
 * Used by backend validation to check if a constantContext entry satisfies
 * a mandatory variable requirement.
 */
export const CONTEXT_KEY_TO_CANONICAL: Record<string, string> = Object.fromEntries(
  Object.entries(CANONICAL_TO_CONTEXT_KEY).map(([k, v]) => [v, k]),
)

/**
 * Key-hint tuples for matching constantContext keys to mandatory variables.
 *
 * Mirrors the `_*_KEY_HINTS` from `personas.py`. Used by
 * `getMissingMandatoryVariables()` to check if a constantContext entry
 * (which may have been auto-detected with a raw column name like "País")
 * satisfies a mandatory variable requirement.
 */
export const MANDATORY_KEY_HINTS: Record<string, readonly string[]> = {
  std_geo_country: ['country', 'region', 'nation', 'land', 'país', 'pais', 'pays', 'paese', 'ülke', 'market'],
  std_dem_gender: ['gender', 'sex', 'sexo', 'género', 'genero', 'geschlecht', 'genre', 'geslacht'],
  std_dem_age: ['age', 'edad', 'âge', 'idade', 'yaş', 'leeftijd'],
}

/* -------------------------------------------------------------------------- */
/*  Shared Satisfaction Checker                                                */
/* -------------------------------------------------------------------------- */

/** Sentinel values that do NOT satisfy strictly-required variables (Country). */
const PLACEHOLDER_VALUES = new Set(['not provided', 'not set', 'n/a', '', 'unchecked', 'checked'])

/** Canonical IDs for geo columns that enable country inference. */
const GEO_SIBLING_IDS = new Set(['std_geo_city', 'std_geo_region'])

/**
 * How a mandatory variable was satisfied — used by both the validation
 * function (button state) and the UI component (badge rendering).
 */
export type SatisfactionReason =
  | 'mapped'        // Column directly mapped to this canonical ID
  | 'override'      // Manual override or auto-detected value in constantContext
  | 'not-provided'  // Conscious choice of "Not provided" (Gender/Age only)
  | 'geo-inferred'  // Country inferred from a mapped city/region column
  | 'fallback'      // B2B age inferred from seniority/experience column
  | null            // Not satisfied

export interface MandatorySatisfactionResult {
  satisfied: boolean
  reason: SatisfactionReason
}

/**
 * Single source of truth: is a mandatory variable satisfied?
 *
 * Used by both `getMissingMandatoryVariables()` (button disabled state)
 * and the unified mapping table's profile rows to prevent divergence
 * between validation and UI display.
 *
 * Checks in priority order:
 *  1. Column directly mapped to this canonical ID
 *  2. B2B age fallback from seniority column (std_prof_seniority)
 *  3. Country geo-inferred from a mapped city/region column
 *  4. constantContext key-hint match (auto-detected or manual override)
 *     - Country: rejects placeholders ("Not provided" etc.)
 *     - Gender/Age: accepts "Not provided" (conscious choice)
 */
export function isMandatorySatisfied(
  canonicalId: string,
  mappings: SegmentationMapping[],
  constantContext?: Record<string, string>,
  manualOverrides?: Record<string, string>,
  audienceType?: string,
): MandatorySatisfactionResult {
  const mappedCanonicalIds = new Set(
    mappings.filter((m) => m.canonicalId != null).map((m) => m.canonicalId!),
  )

  // 1. Directly mapped column
  if (mappedCanonicalIds.has(canonicalId)) {
    return { satisfied: true, reason: 'mapped' }
  }

  // 2. B2B age fallback from seniority
  if (
    canonicalId === 'std_dem_age' &&
    audienceType === 'B2B' &&
    B2B_AGE_FALLBACK_VARIABLES.some((id) => mappedCanonicalIds.has(id))
  ) {
    return { satisfied: true, reason: 'fallback' }
  }

  // 3. Country geo-inferred from city/region
  if (
    canonicalId === 'std_geo_country' &&
    [...mappedCanonicalIds].some((id) => GEO_SIBLING_IDS.has(id))
  ) {
    return { satisfied: true, reason: 'geo-inferred' }
  }

  // 4. Context key-hint match (constantContext + manual overrides)
  const hints = MANDATORY_KEY_HINTS[canonicalId]
  if (hints) {
    const mergedCtx = { ...(constantContext ?? {}), ...(manualOverrides ?? {}) }
    const rejectPlaceholders = STRICTLY_REQUIRED_VARIABLES.includes(canonicalId)

    for (const [key, value] of Object.entries(mergedCtx)) {
      const trimmed = String(value ?? '').trim()
      if (trimmed === '') continue
      if (!hints.some((hint) => key.toLowerCase().includes(hint))) continue

      if (rejectPlaceholders && PLACEHOLDER_VALUES.has(trimmed.toLowerCase())) continue

      // Distinguish "Not provided" from real values for badge rendering
      const isPlaceholder = PLACEHOLDER_VALUES.has(trimmed.toLowerCase())
      return { satisfied: true, reason: isPlaceholder ? 'not-provided' : 'override' }
    }
  }

  return { satisfied: false, reason: null }
}

/**
 * Compute which mandatory variables are still unsatisfied.
 *
 * Delegates to `isMandatorySatisfied()` for each variable — single source
 * of truth shared with the unified mapping table's profile rows.
 *
 * @returns Array of CanonicalVariableEntry for unsatisfied variables
 */
export function getMissingMandatoryVariables(
  mappings: SegmentationMapping[],
  audienceType: AudienceType | '' | undefined,
  constantContext?: Record<string, string>,
  manualOverrides?: Record<string, string>,
): CanonicalVariableEntry[] {
  if (!audienceType) return []

  return (MANDATORY_VARIABLES[audienceType] ?? [])
    .filter((id) => !isMandatorySatisfied(id, mappings, constantContext, manualOverrides, audienceType).satisfied)
    .map((id) => getCanonicalVariable(id))
    .filter((v): v is CanonicalVariableEntry => v != null)
}

/* -------------------------------------------------------------------------- */
/*  Component Props                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Props for the MappingReviewStep component.
 *
 * Used in two contexts:
 * - Wizard step (mode='wizard'): controlled by parent, onConfirm callback
 * - Details page (mode='edit'): lift-state-up, onMappingsChange + onSave
 */
export interface MappingReviewStepProps {
  /** Current mappings to display and edit. */
  mappings: SegmentationMapping[]
  /** Callback when mappings are changed (lift-state-up for edit mode). */
  onMappingsChange: (mappings: SegmentationMapping[]) => void
  /** Component mode: 'wizard' shows Confirm button, 'edit' shows Save button. */
  mode?: 'wizard' | 'edit'
  /** Whether the mapping engine is still running. */
  isLoading?: boolean
  /** Whether auto-detection failed. */
  hasError?: boolean
  /** Whether the user can edit mappings. */
  canEdit?: boolean
  /** Callback for wizard confirmation. Receives manual overrides to merge into constantContext. */
  onConfirm?: (manualOverrides?: Record<string, string>) => void
  /** Callback for saving edits. Receives manual overrides to merge into constantContext. */
  onSave?: (manualOverrides?: Record<string, string>) => void
  /** Whether a save operation is in progress. */
  isSaving?: boolean
  /**
   * Audience type classification ('B2B' or 'B2C').
   * When provided, enables the "Missing Filters" section showing expected
   * canonical variables that are not yet mapped (Sprint 4 — Step 11.1).
   * Also enables mandatory variable validation (mandatory-demographics-plan).
   */
  audienceType?: AudienceType | ''
  /**
   * Sample values per column, keyed by source column name.
   * Shown as a tooltip (info icon) next to each column name to help users
   * identify column contents when manually assigning variables.
   * Data comes from `columnMetadataCache.uniqueValues` or preview rows.
   * Zero cost — no API calls, data already in memory.
   */
  sampleValues?: Record<string, string[]>
  /**
   * Audience-level constant context (auto-detected + manual overrides).
   * Used to check if mandatory variables are already satisfied by constants
   * (e.g., mono-country study where country was collapsed into constantContext).
   * Also used to pre-populate manual override fields with auto-detected values.
   *
   * @see docs/mandatory-demographics-plan.md — Step 5
   */
  constantContext?: Record<string, string>
  /**
   * Callback when constantContext is modified (manual overrides added/edited).
   * Called when the user changes a mandatory variable override.
   */
  onConstantContextChange?: (ctx: Record<string, string>) => void

  /**
   * Distinct values per column (low-cardinality, ≤30 unique values).
   * Used by the value mapping modal to auto-detect
   * demographic value mappings for gender, age, country, ethnicity.
   */
  distinctValuesByColumn?: Record<string, string[]>

  /**
   * Sample rows from preview data — sent to LLM for context-aware inference
   * of unmapped demographic values (e.g., using adjacent columns like city/state).
   */
  previewRows?: any[]

  /**
   * When true, the data preview was column-budgeted (only a subset of columns
   * loaded for performance). Show a degraded-preview banner so the user knows
   * full data will be used on continue.
   * @see docs/audience-ingestion-a-plus-implementation-plan.md — Lane C Sprint 1
   */
  isPartialPreview?: boolean

  /**
   * Per-squad completion status from the parse pipeline.
   * When `profile` is 'failed' or 'degraded', an informational banner is shown
   * above the mandatory section so users understand why auto-detection is limited.
   */
  squadStatus?: Record<string, string>

  /**
   * TwinReadyProcessor stats from the parse response. When provided, a compact
   * strip above the mapping sections shows how many columns were auto-cleaned
   * and their removal breakdown (constants, system, empty, operational, summarized).
   */
  processingStats?: import('@/types/components/audiences/import-wizard').ProcessingStats

  /**
   * Phase 0 column groups from the backend parse pipeline.
   * When provided, used as the primary grouping source for the "Segmentation
   * Filters" section — columns sharing a `groupId` render under a collapsible
   * header using `parentLabel`. Falls back to `groupColumnsBySharedStem` when
   * absent (e.g. small CSVs without codebook analysis).
   */
  columnGroups?: import('@/types/components/audiences/import-wizard').Phase0Group[]
}

/** Canonical IDs for mandatory identity columns (country, gender, age). */
export const IDENTITY_CANONICAL_IDS = new Set([
  'std_geo_country',
  'std_dem_gender',
  'std_dem_age',
])

// Legacy aliases for backward compatibility
export type { CanonicalVariableEntry as CanonicalVariable }
